<script lang="ts">
  import { observe } from "$lib/actions/observe";
  import { SITE_URL } from "$lib/seo/site";
  import { page } from "$app/state";

  // Fields come from the article's Markdown frontmatter, resolved server-side
  // (see $lib/server/blog.ts); `html` is already rendered and sanitized there.
  let { title = "", description = "", date = "", dateLabel = "", author = "", tags = [] as string[], html = "", backHref = "", backText = "" } = $props();

  // BlogPosting structured data for rich results.
  let articleJsonLd = $derived({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    datePublished: date,
    inLanguage: "de-AT",
    author: { "@type": "Organization", name: author || "Practical" },
    publisher: { "@type": "Organization", name: "Practical", url: SITE_URL },
    mainEntityOfPage: SITE_URL + page.url.pathname,
    keywords: tags.join(", ")
  });
</script>

<svelte:head>
  {#if title}
    {@html '<script type="application/ld+json">' + JSON.stringify(articleJsonLd).replace(/</g, "\\u003c") + "</scr" + "ipt>"}
  {/if}
</svelte:head>

<article class="blog-article">
  <div class="container">
    <header class="article-head fade-up" use:observe>
      {#if backHref}
        <a href={backHref} class="article-back">← {backText}</a>
      {/if}
      <div class="blog-meta">
        {#if date}<time datetime={date}>{dateLabel}</time>{/if}
        {#if author}<span> · {author}</span>{/if}
      </div>
      <h1>{title}</h1>
      {#if description}
        <p class="article-lead">{description}</p>
      {/if}
      {#if tags.length}
        <div class="article-tags">
          {#each tags as tag}
            <span class="tag">{tag}</span>
          {/each}
        </div>
      {/if}
    </header>

    <div class="article-body">
      {@html html}
    </div>
  </div>
</article>

<style>
  .blog-article {
    padding-block: clamp(2.5rem, 7vw, 4.5rem);
  }

  .article-head,
  .article-body {
    max-width: 70ch;
    margin-inline: auto;
  }

  .article-back {
    display: inline-block;
    margin-bottom: 1.5rem;
    font-size: 0.875rem;
    color: var(--muted);
    text-decoration: none;
  }

  .article-back:hover {
    color: var(--fg);
  }

  .article-head h1 {
    font-size: clamp(1.875rem, 4.5vw, 3rem);
    line-height: 1.15;
    margin-block: 0.5rem 1rem;
  }

  .article-lead {
    font-size: 1.125rem;
    line-height: 1.6;
    color: var(--muted);
  }

  .article-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 1.25rem;
  }

  .article-body {
    margin-top: clamp(2rem, 5vw, 3rem);
    padding-top: clamp(2rem, 5vw, 3rem);
    border-top: 0.5px solid var(--border);
    font-size: 1.0625rem;
    line-height: 1.75;
  }

  .article-body :global(h2) {
    font-size: clamp(1.375rem, 2.6vw, 1.75rem);
    line-height: 1.25;
    margin-block: 2.75rem 1rem;
  }

  .article-body :global(h3) {
    font-size: 1.125rem;
    margin-block: 2rem 0.75rem;
  }

  .article-body :global(p),
  .article-body :global(ul),
  .article-body :global(ol) {
    margin-block: 0 1.25rem;
  }

  .article-body :global(ul),
  .article-body :global(ol) {
    padding-left: 1.5rem;
  }

  .article-body :global(ul) {
    list-style: disc;
  }

  .article-body :global(ol) {
    list-style: decimal;
  }

  .article-body :global(li::marker) {
    color: var(--accent);
  }

  .article-body :global(li + li) {
    margin-top: 0.5rem;
  }

  .article-body :global(a) {
    color: var(--accent);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .article-body :global(blockquote) {
    margin: 1.75rem 0;
    padding: 1rem 1.25rem;
    border-left: 3px solid var(--accent);
    border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
    background: var(--accent-bg);
  }

  .article-body :global(blockquote p:last-child) {
    margin-bottom: 0;
  }

  .article-body :global(code) {
    font-family: var(--font-mono);
    font-size: 0.9em;
  }

  .article-body :global(hr) {
    margin-block: 3rem 2rem;
    border: none;
    border-top: 0.5px solid var(--border);
  }

  .article-body :global(table) {
    display: block;
    overflow-x: auto;
    width: 100%;
    margin-block: 1.75rem;
    border-collapse: collapse;
    font-size: 0.9375rem;
    line-height: 1.5;
  }

  .article-body :global(th),
  .article-body :global(td) {
    padding: 0.75rem 1rem;
    text-align: left;
    vertical-align: top;
    border-bottom: 0.5px solid var(--border);
  }

  .article-body :global(th) {
    font-weight: 600;
    background: var(--surface);
  }

  .article-body :global(h3 + ul) {
    font-size: 0.9375rem;
  }
</style>
